import { useState, useEffect } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Link } from 'react-router-dom';

export default function Services() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    {
      icon: 'ri-battery-charge-line',
      title: 'Battery Diagnostics & Repair',
      description: 'Comprehensive battery health analysis, cell balancing, and battery pack repair services.',
      features: ['Battery health assessment', 'Cell replacement', 'Thermal management', 'Range optimization'],
      price: 'Starting at $150',
      image: 'https://readdy.ai/api/search-image?query=Professional%20EV%20mechanic%20performing%20electric%20vehicle%20battery%20diagnostics%20with%20advanced%20testing%20equipment%20in%20a%20clean%2C%20modern%20garage%20workshop%20with%20specialized%20EV%20tools%20and%20safety%20equipment%20visible&width=500&height=300&seq=1&orientation=landscape'
    },
    {
      icon: 'ri-charging-pile-2-line',
      title: 'Charging System Service',
      description: 'Complete charging system inspection, repair, and installation for all charging types.',
      features: ['Onboard charger repair', 'Charging port replacement', 'DC fast charging issues', 'Home charger installation'],
      price: 'Starting at $200',
      image: 'https://readdy.ai/api/search-image?query=Skilled%20EV%20technician%20working%20on%20electric%20vehicle%20charging%20system%20components%20and%20charging%20ports%20in%20a%20professional%20automotive%20service%20center%20with%20clean%20workspace%20and%20proper%20lighting&width=500&height=300&seq=2&orientation=landscape'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Electric Motor Service',
      description: 'Expert electric motor diagnostics, maintenance, and repair for optimal performance.',
      features: ['Motor diagnostics', 'Bearing replacement', 'Cooling system service', 'Performance tuning'],
      price: 'Starting at $300',
      image: 'https://readdy.ai/api/search-image?query=Professional%20electric%20motor%20service%20being%20performed%20by%20experienced%20EV%20mechanic%20in%20modern%20automotive%20garage%20with%20specialized%20electric%20motor%20repair%20equipment%20and%20clean%20working%20environment&width=500&height=300&seq=3&orientation=landscape'
    },
    {
      icon: 'ri-cpu-line',
      title: 'Software & Programming',
      description: 'EV software updates, programming, and system calibration services.',
      features: ['Software updates', 'ECU programming', 'System calibration', 'Diagnostic trouble codes'],
      price: 'Starting at $120',
      image: 'https://readdy.ai/api/search-image?query=Expert%20EV%20technician%20performing%20software%20updates%20and%20programming%20on%20electric%20vehicle%20computer%20systems%20using%20advanced%20diagnostic%20equipment%20in%20professional%20automotive%20workshop&width=500&height=300&seq=4&orientation=landscape'
    },
    {
      icon: 'ri-plug-line',
      title: 'High-Voltage Systems',
      description: 'Safe high-voltage electrical system diagnostics and repair with certified technicians.',
      features: ['HV safety inspection', 'Inverter service', 'DC-DC converter repair', 'Electrical troubleshooting'],
      price: 'Starting at $250',
      image: 'https://readdy.ai/api/search-image?query=Certified%20EV%20technician%20working%20on%20high-voltage%20electrical%20systems%20with%20proper%20safety%20equipment%20and%20protocols%20in%20a%20professional%20electric%20vehicle%20service%20center&width=500&height=300&seq=5&orientation=landscape'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Preventive Maintenance',
      description: 'Regular EV maintenance to keep your electric vehicle running efficiently.',
      features: ['Brake service', 'Tire rotation', 'Cabin air filter', 'Coolant system service'],
      price: 'Starting at $80',
      image: 'https://readdy.ai/api/search-image?query=Professional%20EV%20preventive%20maintenance%20service%20including%20brake%20inspection%20and%20tire%20rotation%20being%20performed%20in%20clean%20automotive%20service%20bay%20with%20modern%20equipment&width=500&height=300&seq=6&orientation=landscape'
    }
  ];

  const evBrands = [
    { name: 'Tesla', logo: 'ri-car-line' },
    { name: 'Nissan Leaf', logo: 'ri-leaf-line' },
    { name: 'Chevrolet Bolt', logo: 'ri-flashlight-line' },
    { name: 'BMW i-Series', logo: 'ri-car-line' },
    { name: 'Audi e-tron', logo: 'ri-car-line' },
    { name: 'Ford Mustang Mach-E', logo: 'ri-car-line' },
    { name: 'Volkswagen ID', logo: 'ri-car-line' },
    { name: 'Hyundai IONIQ', logo: 'ri-car-line' }
  ];

  return (
    <div className="min-h-screen bg-[#f5f1e8]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Professional Electric Vehicle Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Comprehensive EV repair and maintenance services for all electric vehicle makes and models. 
              Expert technicians with specialized EV training and equipment.
            </p>
            <Link 
              to="/appointment" 
              className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer inline-block"
            >
              Schedule EV Service
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Our EV Services
            </h2>
            <p className="text-xl text-black max-w-3xl mx-auto">
              From battery diagnostics to software updates, we provide complete electric vehicle services 
              to keep your EV running at peak performance.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-[#f5f1e8] border border-green-200 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mr-4">
                      <i className={`${service.icon} text-2xl text-green-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-green-600">{service.title}</h3>
                      <p className="text-sm text-green-500 font-medium">{service.price}</p>
                    </div>
                  </div>
                  <p className="text-black mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-black">
                        <i className="ri-check-line text-green-600 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EV Brands Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Electric Vehicle Brands We Service
            </h2>
            <p className="text-xl text-black">
              Our certified EV technicians work on all major electric vehicle brands
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {evBrands.map((brand, index) => (
              <div key={index} className="text-center p-6 bg-[#f5f1e8] border border-green-200 rounded-lg hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className={`${brand.logo} text-3xl text-green-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-black">{brand.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 mb-4">
              Our EV Service Process
            </h2>
            <p className="text-xl text-black">
              Professional, safe, and efficient electric vehicle service
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 text-white rounded-full mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Safety Check</h3>
              <p className="text-black">High-voltage safety inspection and proper isolation procedures</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 text-white rounded-full mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Diagnostics</h3>
              <p className="text-black">Advanced EV diagnostic testing and system analysis</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 text-white rounded-full mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Repair</h3>
              <p className="text-black">Expert repair using OEM parts and specialized EV tools</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 text-white rounded-full mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-semibold text-green-600 mb-2">Testing</h3>
              <p className="text-black">Comprehensive testing and quality assurance verification</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Service Your Electric Vehicle?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Book your EV service appointment today and experience professional electric vehicle care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/appointment" 
              className="bg-white text-green-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Book EV Service
            </Link>
            <a 
              href="tel:18005700229" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-green-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call 1-800-570-0229
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
